import React, { useEffect, useState, useRef } from 'react';
import { Users, CheckCircle2, Clock, Award } from 'lucide-react';

const stats = [
  {
    icon: Users,
    value: 1247,
    suffix: '+',
    label: 'Pelanggan Dibantu',
    color: 'text-amber-400'
  },
  {
    icon: CheckCircle2,
    value: 98,
    suffix: '%',
    label: 'Kepuasan Pelanggan',
    color: 'text-emerald-300'
  },
  {
    icon: Clock,
    value: 24,
    suffix: ' Jam',
    label: 'Respons Pantas',
    color: 'text-amber-400'
  },
  {
    icon: Award,
    value: 5,
    suffix: '+ Tahun',
    label: 'Pengalaman',
    color: 'text-emerald-300'
  }
];

const useCountUp = (target, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;
    
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [target, duration, shouldStart]);

  return count;
};

const StatCard = ({ stat, shouldAnimate }) => {
  const count = useCountUp(stat.value, 2000, shouldAnimate);
  const IconComponent = stat.icon;
  
  return (
    <div className="text-center group">
      <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-md rounded-full mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300 border border-white/20">
        <IconComponent className={`h-7 w-7 sm:h-8 sm:w-8 ${stat.color}`} />
      </div>
      <div className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-2 ${stat.color}`}>
        {count.toLocaleString()}{stat.suffix}
      </div>
      <p className="text-emerald-50 text-xs sm:text-sm font-medium">
        {stat.label}
      </p>
    </div>
  );
};

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-12 sm:py-16 bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-72 h-72 bg-amber-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Title */}
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
            Dipercayai Ramai Penjawat Awam
          </h2>
          <p className="text-emerald-100 text-sm sm:text-base">
            Angka yang membuktikan kualiti perkhidmatan kami
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 max-w-5xl mx-auto">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} shouldAnimate={isVisible} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
